import { useState, useEffect, useRef } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { fetchSearchResults } from "@/api/search";
import type { SearchResult } from "@/types/search";

interface UseSearchReturn {
  results: SearchResult | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Searches via the v2 search API with debounced input and request cancellation.
 * Returns null results when the query is empty.
 */
export function useSearch(query: string, debounceMs = 300): UseSearchReturn {
  const [results, setResults] = useState<SearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debouncedQuery = useDebounce(query, debounceMs);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortRef.current?.abort();

    const trimmed = debouncedQuery.trim();
    if (!trimmed) {
      setResults(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    setError(null);

    fetchSearchResults(trimmed, { signal: controller.signal }).then(
      (result) => {
        if (controller.signal.aborted) return;
        if (result.ok) {
          setResults(result.data);
          setError(null);
        } else {
          setError(result.error);
        }
        setIsLoading(false);
      }
    );

    return () => controller.abort();
  }, [debouncedQuery]);

  return { results, isLoading, error };
}
